import React, { useId, useRef } from 'react';
import {
  useCarouselNavigation,
  useEdgeVisibility,
} from '@noths/polaris-design-system-candidate-components';
import { IconButton } from '@noths/polaris-design-system-core-components';

import type { DepartmentCardProps } from 'src/components/molecules/DepartmentCard/DepartmentCard';
import { DepartmentCard } from 'src/components/molecules/DepartmentCard/DepartmentCard';
import * as styles from './DepartmentCarousel.styles';

export interface DepartmentCarouselProps {
  departments: DepartmentCardProps[];
  heading: string;
}

/** A swipeable row of department cards, with arrows where there is no touchscreen. */
export const DepartmentCarousel = ({ departments, heading }: DepartmentCarouselProps) => {
  const headingId = useId();
  const trackId = useId();
  const trackRef = useRef<HTMLUListElement>(null);
  const { isAtEnd, isAtStart } = useEdgeVisibility(trackRef, departments.length);
  const { scrollNext, scrollPrev } = useCarouselNavigation(trackRef, undefined);

  return (
    <section aria-labelledby={headingId} aria-roledescription="carousel">
      <header css={styles.header}>
        <h2 css={styles.heading} id={headingId}>
          {heading}
        </h2>
        <div aria-label="Carousel controls" css={styles.controls} role="group">
          <IconButton
            aria-controls={trackId}
            aria-label="Previous departments"
            compact
            disabled={isAtStart}
            icon="actionBack"
            onClick={scrollPrev}
          />
          <IconButton
            aria-controls={trackId}
            aria-label="Next departments"
            compact
            disabled={isAtEnd}
            icon="actionForward"
            onClick={scrollNext}
          />
        </div>
      </header>
      <ul css={styles.track} id={trackId} ref={trackRef}>
        {departments.map((department) => (
          <li css={styles.item} key={department.href}>
            <DepartmentCard {...department} />
          </li>
        ))}
      </ul>
    </section>
  );
};
